import { useEffect, useState } from "react";
import { KeyRound, Loader2, Save, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { apiFetch } from "./api";

export const Settings = ({ token, onAuthError }) => {
    const [content, setContent] = useState({});
    const [checkoutUrl, setCheckoutUrl] = useState("");
    const [notifyEmail, setNotifyEmail] = useState("");
    const [ga4Id, setGa4Id] = useState("");
    const [metaPixelId, setMetaPixelId] = useState("");
    const [savingUrl, setSavingUrl] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pw, setPw] = useState({ current_password: "", new_password: "" });
    const [savingPw, setSavingPw] = useState(false);

    const submitToSearch = async () => {
        setSubmitting(true);
        try {
            const data = await apiFetch("/seo/indexnow", { token, method: "POST" });
            toast.success("Pages submitted to search engines", {
                description: `${data.submitted} URLs sent to Bing (which powers ChatGPT search) for ${data.host}.`,
            });
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Submission failed", { description: err.message });
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        apiFetch("/site-config")
            .then((data) => {
                setContent(data.content || {});
                const s = data.settings || {};
                setCheckoutUrl(s.checkout_url || "");
                setNotifyEmail(s.notify_email || "");
                setGa4Id(s.ga4_id || "");
                setMetaPixelId(s.meta_pixel_id || "");
            })
            .catch(() => {});
    }, []);

    const saveUrl = async (e) => {
        e.preventDefault();
        setSavingUrl(true);
        try {
            await apiFetch("/site-config", {
                token,
                method: "PUT",
                body: {
                    content,
                    settings: {
                        checkout_url: checkoutUrl.trim(),
                        notify_email: notifyEmail.trim(),
                        ga4_id: ga4Id.trim(),
                        meta_pixel_id: metaPixelId.trim(),
                    },
                },
            });
            toast.success("Settings saved", {
                description: "Your changes are live on the site.",
            });
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Save failed", { description: err.message });
        } finally {
            setSavingUrl(false);
        }
    };

    const changePw = async (e) => {
        e.preventDefault();
        setSavingPw(true);
        try {
            await apiFetch("/auth/change-password", {
                token,
                method: "POST",
                body: pw,
            });
            toast.success("Password changed", {
                description: "Use your new password next time you sign in.",
            });
            setPw({ current_password: "", new_password: "" });
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Could not change password", {
                description: err.message,
            });
        } finally {
            setSavingPw(false);
        }
    };

    const inputCls =
        "mt-1.5 h-11 w-full rounded-lg border border-white/15 bg-[#121722] px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-400";
    const labelCls =
        "text-xs font-semibold uppercase tracking-wider text-slate-500";

    return (
        <div data-testid="admin-settings-tab" className="flex flex-col gap-8">
            <form
                onSubmit={saveUrl}
                className="rounded-2xl border border-white/10 bg-[#0C1019] p-6"
            >
                <h3 className="font-display text-base font-semibold text-sky-400">
                    Site Settings
                </h3>
                <div className="mt-4 flex flex-col gap-5">
                    <div>
                        <label className={labelCls}>Checkout Popup Link</label>
                        <p className="mt-0.5 text-xs text-slate-500">
                            Loaded inside the quote popup. Leave empty to use the
                            built-in default.
                        </p>
                        <input
                            data-testid="settings-checkout-url-input"
                            type="url"
                            placeholder="https://advantashield.dealer.fi-portal.com/create-agreement?token=…"
                            value={checkoutUrl}
                            onChange={(e) => setCheckoutUrl(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>
                            Lead Notification Email
                        </label>
                        <p className="mt-0.5 text-xs text-slate-500">
                            Every new dealer inquiry is emailed here instantly.
                            Leave empty to use the admin account email.
                        </p>
                        <input
                            data-testid="settings-notify-email-input"
                            type="email"
                            placeholder="you@yourdealership.com"
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label className={labelCls}>
                                Google Analytics 4 ID
                            </label>
                            <p className="mt-0.5 text-xs text-slate-500">
                                From GA4 → Admin → Data Streams. Starts with G-.
                            </p>
                            <input
                                data-testid="settings-ga4-input"
                                type="text"
                                placeholder="G-XXXXXXXXXX"
                                value={ga4Id}
                                onChange={(e) => setGa4Id(e.target.value)}
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Meta Pixel ID</label>
                            <p className="mt-0.5 text-xs text-slate-500">
                                From Meta Events Manager. Numbers only.
                            </p>
                            <input
                                data-testid="settings-meta-pixel-input"
                                type="text"
                                placeholder="1234567890123456"
                                value={metaPixelId}
                                onChange={(e) => setMetaPixelId(e.target.value)}
                                className={inputCls}
                            />
                        </div>
                    </div>
                </div>
                <button
                    data-testid="settings-save-url-btn"
                    type="submit"
                    disabled={savingUrl}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-2.5 text-sm font-bold text-[#0A0D14] transition-colors hover:bg-sky-300 disabled:opacity-60"
                >
                    {savingUrl ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="h-4 w-4" />
                    )}
                    Save Settings
                </button>
            </form>

            <div className="rounded-2xl border border-white/10 bg-[#0C1019] p-6">
                <h3 className="font-display text-base font-semibold text-sky-400">
                    Search Engine Submission
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                    Instantly submits every page — homepage, guide, blog articles, and
                    all 50 state pages — to Bing via IndexNow. Bing's index powers
                    ChatGPT search. Run this after deploying to advantashield.com and
                    whenever you publish new articles.
                </p>
                <button
                    data-testid="settings-indexnow-btn"
                    onClick={submitToSearch}
                    disabled={submitting}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-2.5 text-sm font-bold text-[#0A0D14] transition-colors hover:bg-sky-300 disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                    {submitting ? "Submitting…" : "Submit All Pages to Search"}
                </button>
            </div>

            <form
                onSubmit={changePw}
                className="rounded-2xl border border-white/10 bg-[#0C1019] p-6"
            >
                <h3 className="font-display text-base font-semibold text-sky-400">
                    Change Admin Password
                </h3>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input
                        data-testid="settings-current-password-input"
                        type="password"
                        required
                        placeholder="Current password"
                        value={pw.current_password}
                        onChange={(e) =>
                            setPw((p) => ({
                                ...p,
                                current_password: e.target.value,
                            }))
                        }
                        className={inputCls}
                    />
                    <input
                        data-testid="settings-new-password-input"
                        type="password"
                        required
                        minLength={8}
                        placeholder="New password (8+ characters)"
                        value={pw.new_password}
                        onChange={(e) =>
                            setPw((p) => ({ ...p, new_password: e.target.value }))
                        }
                        className={inputCls}
                    />
                </div>
                <button
                    data-testid="settings-change-password-btn"
                    type="submit"
                    disabled={savingPw}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-sky-400/50 px-6 py-2.5 text-sm font-bold text-sky-300 transition-colors hover:bg-sky-400/10 disabled:opacity-60"
                >
                    {savingPw ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <KeyRound className="h-4 w-4" />
                    )}
                    Change Password
                </button>
            </form>
        </div>
    );
};
